import { useEffect, useMemo, useState } from "react"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTrigger } from "@/components/ui/dialog"
import type { RiskPoint, RiskThresholds } from "./RiskStratificationChart"
import BorderedIconWrapper from "../../common/BorderIconWrapper"
import RiskIcon from "@/assets/icons/risk-stratification.svg"
import { Typography } from "../../ui/typography"
import { Chip } from "../../common/Chip"
import { RiskStratificationChart } from "./RiskStratificationChart"
import { DialogMultiSelectDropdown } from "@/components/common/DialogMultiSelectDropdown"
import { filterRiskChartData } from "@/utils/riskChartUtils"
import CrossIcon from "@/assets/svg/crossIcon.svg"
import CrossDarkIcon from "@/assets/svg/crossDarkIcon.svg"

type RiskStratificationDialogProps = {
  trigger?: React.ReactNode
  riskScore: number
  riskCategory?: string
  data: RiskPoint[]
  thresholds?: RiskThresholds
  open?: boolean
  onOpenChange?: (open: boolean) => void
  currentYear?: number
  availableYears?: number[]
  chartLoading?: boolean
  chartError?: string | null
  lastUpdated?: string
}

const RiskStratificationDialog = ({
  trigger,
  riskScore,
  riskCategory,
  data,
  thresholds,
  open,
  onOpenChange,
  currentYear,
  availableYears = [],
  chartLoading = false,
  chartError = null,
  lastUpdated,
}: RiskStratificationDialogProps) => {
  const [selectedYears, setSelectedYears] = useState<string[]>([])

  const yearOptions = useMemo(() => {
    // Use availableYears from API if provided, otherwise extract from data
    if (availableYears.length > 0) {
      return availableYears
        .slice()
        .sort((a, b) => a - b)
        .map((year) => ({
          label: year.toString(),
          value: year.toString(),
        }))
    }

    const uniqueYears = Array.from(
      new Set(
        data
          .map((point) => extractYear(point.date))
          .filter((year): year is string => Boolean(year))
      )
    )
      .sort((a, b) => Number(a) - Number(b))
      .map((year) => ({ label: year, value: year }))

    return uniqueYears
  }, [data, availableYears])

  // Reset selectedYears when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedYears([])
    }
  }, [open])

  useEffect(() => {
    if (!yearOptions.length) return

    // When dialog opens and no years are selected, default to empty (which will show current year logic)
    setSelectedYears((prev) => {
      const stillAvailable = prev.filter((value) =>
        yearOptions.some((option) => option.value === value)
      )
      // Don't auto-select all years - let the filter function handle default behavior
      return stillAvailable
    })
  }, [yearOptions])

  const resolvedThresholds = useMemo<RiskThresholds>(
    () => ({
      low: thresholds?.low ?? 0,
      medium: thresholds?.medium ?? 30,
      high: thresholds?.high ?? 60,
      max: thresholds?.max ?? 100,
    }),
    [thresholds]
  )

  const getScoreCategory = (score: number) => {
    if (score >= resolvedThresholds.high) return "High"
    if (score >= resolvedThresholds.medium) return "Medium"
    return "Low"
  }

  const getChipVariant = (
    category: string
  ): "success" | "warning" | "danger" => {
    const normalized = category.toLowerCase()
    if (normalized === "low") return "success"
    if (normalized === "medium") return "warning"
    return "danger"
  }

  const displayedCategory = riskCategory ?? getScoreCategory(riskScore)

  const filteredChartData = useMemo(() => {
    if (!data.length) return []

    // Use utility function to filter data
    // When currentYear is provided and no years selected, it will show current year + previous quarters
    if (currentYear) {
      return filterRiskChartData(data, currentYear, selectedYears)
    }

    // Fallback to old logic if currentYear is not available
    if (!selectedYears.length) {
      return data
    }
    return data.filter((point) => {
      const year = extractYear(point.date)
      if (!year) return true
      return selectedYears.includes(year)
    })
  }, [data, selectedYears, currentYear])

  const chartDataToRender = filteredChartData.length ? filteredChartData : data

  const handleYearSelectionChange = (values: string[]) => {
    setSelectedYears(values)
  }

  const handleChipRemove = (year: string) => {
    setSelectedYears((prev) => prev.filter((item) => item !== year))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent 
        className="gap-0 p-0 w-[96%] md:w-[98%] lg:w-full max-w-5xl max-h-[80%] overflow-auto bg-grey-100 dark:bg-blue-900 !animate-none data-[state=open]:fade-in data-[state=open]:zoom-in-95 rounded-[6px]"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="min-h-12 py-[9px] px-[18px] border-b border-grey-300 dark:border-grey-600 rounded-tl-[6px] rounded-tr-[6px] rounded-bl-[1px] rounded-br-[1px]">
          <div className="flex items-center justify-between gap-4 h-full">
            <div className="flex items-center gap-2">
              <BorderedIconWrapper>
                <img
                  src={RiskIcon}
                  alt='riskStratification'
                  className='w-4 h-4'
                />
              </BorderedIconWrapper>
              <div className="flex flex-col">
                <Typography variant="semibold-16">
                  Risk Stratification
                </Typography>
              {lastUpdated && (
                <div className="max-w-full">
                  <Typography
                    className="text-grey-600 dark:text-grey-500 font-normal whitespace-nowrap max-w-full"
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      fontStyle: "normal",
                      fontSize: "12px",
                      lineHeight: "20px",
                      letterSpacing: "0px",
                    }}
                  >
                    Last updated on {lastUpdated}
                  </Typography>
                </div>
              )}

              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block">
                <Chip
                  label={"Risk Score"}
                  value={`${String(riskScore)} %`}
                  variant={getChipVariant(displayedCategory)}
                />
              </div>
              <DialogClose asChild>
                <div
                  role='button'
                  aria-label='Close'
                  onClick={() => onOpenChange?.(false)}
                  className='
                  h-[30px] w-[30px]
                  bg-grey-200
                  hover:bg-grey-300
                  dark:bg-[hsl(var(--blue-500))]
                  dark:hover:bg-gray-600
                  text-muted-foreground
                  hover:none
                  flex items-center justify-center
                  rounded-md
                  cursor-pointer
                  transition-all
                   '
                >
                  <img
                    src={CrossIcon}
                    alt='crossicon'
                    className=' block dark:hidden'
                  />
                  <img
                    src={CrossDarkIcon}
                    alt='crossicon'
                    className='hidden dark:block'
                  />
                </div>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        <div className="px-3 py-3 overflow-auto flex-1">
          <div className="space-y-4">
          <div className="flex w-full flex-row flex-wrap items-center justify-between gap-6 xs:pl-1 md:pl-2">
            <div className="flex flex-wrap items-center gap-6 text-[var(--risk-legend-text)]">
              <LegendDot colorVar="--risk-low-color" label="Low" />
              <LegendDot colorVar="--risk-medium-color" label="Medium" />
              <LegendDot colorVar="--risk-high-color" label="High" />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <DialogMultiSelectDropdown
                options={yearOptions}
                selectedValues={selectedYears}
                onChange={handleYearSelectionChange}
                placeholder="Select Years"
              />
            </div>
          </div>

          {selectedYears.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 xs:pl-2 md:pl-14">
              {selectedYears.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => handleChipRemove(year)}
                  className="px-3 py-1 rounded border text-sm flex items-center gap-2 transition-colors"
                  style={{
                    borderColor: "var(--risk-chip-border)",
                    backgroundColor: "var(--risk-chip-bg)",
                    color: "var(--risk-chip-text)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--risk-chip-hover-bg)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--risk-chip-bg)";
                  }}
                >
                  {year}
                  <i className="ri-close-line text-xs" />
                </button>
              ))}
            </div>
          )}

            <div className="w-full">
              {(() => {
                if (chartLoading) {
                  return (
                    <div className="flex items-center justify-center h-[400px]">
                      <Typography variant="medium-14" className="text-grey-600 dark:text-grey-500">
                        Loading chart data...
                      </Typography>
                    </div>
                  )
                }
                if (chartError) {
                  return (
                    <div className="flex items-center justify-center h-[400px]">
                      <Typography variant="medium-14" className="text-red-500 dark:text-red-400">
                        {chartError}
                      </Typography>
                    </div>
                  )
                }
                return (
                  <RiskStratificationChart data={chartDataToRender} thresholds={resolvedThresholds} />
                )
              })()}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const LegendDot = ({ colorVar, label }: { colorVar: string; label: string }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-[var(--risk-legend-text)]">
    <span
      className="h-4 w-4 rounded-sm"
      style={{ backgroundColor: `var(${colorVar})` }}
      aria-hidden="true"
    />
    <span>{label}</span>
  </div>
)

const extractYear = (dateLabel: string): string | null => {
  const match = dateLabel.match(/(19|20)\d{2}/)
  return match ? match[0] : null
}

export type { RiskStratificationDialogProps }
export { RiskStratificationDialog }